/**
 * My Apps, as a launcher rather than as a screen that fetches.
 *
 * ## Why this exists
 *
 * `MyAppsPage` used to own its listing in component state: `null` until
 * `computeClient.list()` came back, which meant every cold start, every return
 * from Discover's expanded pane (which unmounts My Apps) and every reload
 * showed "Loading your apps…" before showing anybody their apps. A home screen
 * is not a report. An OS launcher draws the last thing it knew and reconciles
 * afterwards, and that is what this store does: read the last snapshot
 * synchronously, paint it, then revalidate.
 *
 * ## What is stored, and what is deliberately not
 *
 * Launcher metadata only — the name, icon, role and URL a tile needs. Never
 * `runtime_state` (a fact about right now, which a snapshot cannot honestly
 * carry), never saved data, never invite tokens, never anything a signed-out
 * device should not still be holding. `LAUNCHER_FIELDS` is the whole list, and
 * `projectLauncherApp` is the only way instances enter storage.
 *
 * The trade-off this accepts, stated plainly: if the cookie was revoked
 * elsewhere, this device shows the previous app NAMES for the few hundred
 * milliseconds until the session check answers. That is inseparable from
 * painting before the network. It is acceptable because what is cached is
 * metadata a person already saw, every action stays server-authorized, and an
 * explicit sign-out erases it.
 *
 * ## Keyed by environment AND account
 *
 * One key per (API origin, user). Staging and production disagree about which
 * apps exist, and two people sharing a browser must never inherit each
 * other's tiles. A separate `last-user` pointer is what makes the COLD read
 * possible: at boot there is no session yet, so the only way to know whose
 * snapshot to paint is to have written down who it was last time.
 *
 * ## Failure is not emptiness
 *
 * A refresh that fails leaves the tiles alone and records `error`. The old
 * behaviour — `setGroups([])` on a rejected fetch — meant one flaky request
 * emptied somebody's home screen. Only a first load with nothing cached is
 * allowed to be an error state.
 */

import { useSyncExternalStore } from "react";

import { accountApiBase } from "@/app/connection";
import { computeClient } from "@/components/personal-apps/computeClient";
import type { PersonalApp } from "@/components/personal-apps/types";

/** Bumped only when the stored SHAPE changes; an old key is ignored, not migrated. */
const SNAPSHOT_KEY_PREFIX = "ato.my-apps.snapshot.v1";
const LAST_USER_KEY_PREFIX = "ato.my-apps.last-user.v1";

/**
 * How recently a refresh has to have happened for another one to be skipped.
 *
 * Mount, becoming visible, `pageshow` and `online` can all fire within one
 * gesture (restoring a backgrounded tab fires several). Coalescing them is
 * what keeps "revalidate on every signal" from meaning "fetch four times".
 */
export const MY_APPS_REFRESH_INTERVAL_MS = 8_000;

/**
 * What this device knows about the session RIGHT NOW.
 *
 * Three states rather than a `signedIn` boolean, because the launcher paints
 * during the first one: `pending` means "the session check has not answered
 * yet, and these tiles come from the last time it did". Nothing in `pending`
 * may claim the person is signed in — it gates mutations and suppresses the
 * refresh error a not-yet-established cookie can legitimately produce.
 */
export type MyAppsAuthState = "pending" | "verified" | "signed_out";

export interface MyAppsSnapshot {
  version: 1;
  userId: string;
  savedAt: number;
  instances: PersonalApp[];
}

export interface MyAppsState {
  /** Whose apps these are, or "" when nothing has been claimed yet. */
  userId: string;
  /** `null` means nothing is known yet — the only state that may show a loader. */
  instances: PersonalApp[] | null;
  /** When `instances` was last agreed with the server, or read from storage. */
  savedAt: number;
  refreshing: boolean;
  /**
   * The last refresh failure. Kept ALONGSIDE `instances`: with tiles on
   * screen this is "couldn't refresh", and only with `instances === null` is
   * it the screen's error state.
   */
  error: string | null;
}

/**
 * The only fields that reach storage.
 *
 * Keeping this list explicit — rather than persisting whatever `list()`
 * returned — is what stops a future API field (a presence count, a token, a
 * runtime detail) from silently becoming durable client-side state.
 */
const LAUNCHER_FIELDS = [
  "instance_id",
  "compute_id",
  "schema_id",
  "display_name",
  "default_display_name",
  "icon_color",
  "icon_glyph",
  "icon_image_path",
  "listing_icon_url",
  "role",
  "origin_kind",
  "origin_key",
  "host_slug",
  "app_url",
  "status",
  "created_at",
  "updated_at",
] as const satisfies readonly (keyof PersonalApp)[];

/** One instance, reduced to what a tile draws. */
export function projectLauncherApp(app: PersonalApp): PersonalApp {
  const out: Record<string, unknown> = {};
  for (const field of LAUNCHER_FIELDS) out[field] = app[field];
  return out as unknown as PersonalApp;
}

const EMPTY_STATE: MyAppsState = {
  userId: "",
  instances: null,
  savedAt: 0,
  refreshing: false,
  error: null,
};

let state: MyAppsState = EMPTY_STATE;
const listeners = new Set<() => void>();
let inflight: Promise<void> | null = null;
let lastRefreshAt = 0;
let bootstrapped = false;

function emit(): void {
  for (const listener of listeners) listener();
}

function setState(patch: Partial<MyAppsState>): void {
  state = { ...state, ...patch };
  emit();
}

/**
 * `localStorage` when it is genuinely usable.
 *
 * Merely existing is not enough: Safari private mode has thrown on ACCESS, and
 * an embedded context can have storage blocked by policy. Every failure path
 * here degrades to "no cache", never to a broken launcher.
 */
function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function snapshotKey(userId: string): string {
  return `${SNAPSHOT_KEY_PREFIX}:${accountApiBase()}:${userId}`;
}

function lastUserKey(): string {
  return `${LAST_USER_KEY_PREFIX}:${accountApiBase()}`;
}

function isLauncherApp(value: unknown): value is PersonalApp {
  if (!value || typeof value !== "object") return false;
  const app = value as Record<string, unknown>;
  return (
    typeof app.instance_id === "string" &&
    app.instance_id.length > 0 &&
    typeof app.compute_id === "string" &&
    app.compute_id.length > 0 &&
    typeof app.app_url === "string" &&
    typeof app.display_name === "string"
  );
}

function readSnapshot(userId: string): MyAppsSnapshot | null {
  const storage = safeStorage();
  if (!storage || !userId) return null;
  let raw: string | null = null;
  try {
    raw = storage.getItem(snapshotKey(userId));
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const candidate = parsed as Partial<MyAppsSnapshot>;
    // A shape from a version this build does not know is ignored, not
    // guessed at: a half-understood launcher is worse than a loader.
    if (candidate.version !== 1) return null;
    if (candidate.userId !== userId) return null;
    if (!Array.isArray(candidate.instances)) return null;
    if (!candidate.instances.every(isLauncherApp)) return null;
    return {
      version: 1,
      userId,
      savedAt:
        typeof candidate.savedAt === "number" && candidate.savedAt > 0
          ? candidate.savedAt
          : 0,
      instances: candidate.instances.map(projectLauncherApp),
    };
  } catch {
    return null;
  }
}

function writeSnapshot(userId: string, instances: PersonalApp[]): void {
  const storage = safeStorage();
  // No account, no durable copy: an unidentified snapshot could be painted
  // for the wrong person on the next cold start.
  if (!storage || !userId) return;
  const snapshot: MyAppsSnapshot = {
    version: 1,
    userId,
    savedAt: Date.now(),
    instances: instances.map(projectLauncherApp),
  };
  try {
    storage.setItem(snapshotKey(userId), JSON.stringify(snapshot));
    storage.setItem(lastUserKey(), userId);
  } catch {
    // Quota or a blocked store. This session is still correct on screen; it
    // just will not paint instantly next time.
  }
}

function forgetSnapshot(userId: string): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    if (userId) storage.removeItem(snapshotKey(userId));
    storage.removeItem(lastUserKey());
  } catch {
    // Nothing to do and nothing worth reporting.
  }
}

/**
 * Paint whoever used this device last, synchronously.
 *
 * Runs at most once per page load, on the first read. Deliberately lazy rather
 * than at module import: `accountApiBase()` is resolved per call by design
 * (the Desktop shell injects it, and tests stub it), so reading storage at
 * import time could key the cache off the wrong environment.
 */
function bootstrap(): void {
  if (bootstrapped) return;
  bootstrapped = true;
  const storage = safeStorage();
  if (!storage) return;
  let lastUser: string | null = null;
  try {
    lastUser = storage.getItem(lastUserKey());
  } catch {
    return;
  }
  if (!lastUser) return;
  const snapshot = readSnapshot(lastUser);
  if (!snapshot) return;
  state = {
    userId: snapshot.userId,
    instances: snapshot.instances,
    savedAt: snapshot.savedAt,
    refreshing: false,
    error: null,
  };
}

export function getMyAppsState(): MyAppsState {
  bootstrap();
  return state;
}

/** Whether `/` has something to paint before any request answers. */
export function hasCachedMyApps(): boolean {
  return getMyAppsState().instances !== null;
}

export function subscribeMyApps(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The session came back. Claim the cache for this account.
 *
 * Same user: keep painting and let the caller revalidate. Different user: the
 * previous account's tiles leave the screen immediately — being fast must
 * never mean showing somebody else's apps — and that account's own snapshot,
 * if any, takes their place.
 */
export function adoptMyAppsUser(userId: string): void {
  bootstrap();
  if (!userId || state.userId === userId) return;
  const snapshot = readSnapshot(userId);
  lastRefreshAt = 0;
  state = {
    userId,
    instances: snapshot?.instances ?? null,
    savedAt: snapshot?.savedAt ?? 0,
    refreshing: state.refreshing,
    error: null,
  };
  const storage = safeStorage();
  try {
    storage?.setItem(lastUserKey(), userId);
  } catch {
    // The pointer is an optimization; failing to write it only costs the
    // next cold start its instant paint.
  }
  emit();
}

/**
 * Signed out, explicitly. Nothing about the previous account stays readable.
 */
export function clearMyApps(): void {
  bootstrap();
  forgetSnapshot(state.userId);
  lastRefreshAt = 0;
  state = EMPTY_STATE;
  emit();
}

/** The server's answer, as the new truth. */
export function applyMyAppsList(instances: PersonalApp[]): void {
  bootstrap();
  const projected = instances.map(projectLauncherApp);
  writeSnapshot(state.userId, projected);
  setState({ instances: projected, savedAt: Date.now(), error: null });
}

/**
 * One instance this device just created or re-read.
 *
 * Write-through, so an App that was just added is already on the home screen
 * when a person arrives there — instead of appearing when a background
 * refresh happens to land.
 */
export function upsertMyApp(instance: PersonalApp): void {
  bootstrap();
  const projected = projectLauncherApp(instance);
  const current = state.instances ?? [];
  const index = current.findIndex(
    (app) => app.instance_id === projected.instance_id,
  );
  const next =
    index === -1
      ? [projected, ...current]
      : current.map((app, at) => (at === index ? projected : app));
  writeSnapshot(state.userId, next);
  setState({ instances: next, savedAt: Date.now() });
}

/** Every set of data for one App is gone. */
export function removeMyAppCompute(computeId: string): void {
  bootstrap();
  if (!state.instances) return;
  const next = state.instances.filter((app) => app.compute_id !== computeId);
  writeSnapshot(state.userId, next);
  setState({ instances: next, savedAt: Date.now() });
}

/** Specific instances are gone — what `removeApp` reports having deleted. */
export function removeMyAppInstances(instanceIds: readonly string[]): void {
  bootstrap();
  if (!state.instances) return;
  const gone = new Set(instanceIds);
  const next = state.instances.filter((app) => !gone.has(app.instance_id));
  writeSnapshot(state.userId, next);
  setState({ instances: next, savedAt: Date.now() });
}

/**
 * Revalidate in the background.
 *
 * Single-flight, and coalesced within `MY_APPS_REFRESH_INTERVAL_MS` unless
 * forced. `force` is for the moments a person just changed something and is
 * entitled to see the server agree.
 */
export function refreshMyApps(
  options: { force?: boolean } = {},
): Promise<void> {
  bootstrap();
  if (inflight) return inflight;
  if (
    !options.force &&
    lastRefreshAt > 0 &&
    Date.now() - lastRefreshAt < MY_APPS_REFRESH_INTERVAL_MS
  ) {
    return Promise.resolve();
  }
  setState({ refreshing: true });
  const run = computeClient
    .list()
    .then(({ instances }) => {
      applyMyAppsList(instances);
    })
    .catch((cause: unknown) => {
      // The tiles stay. A launcher that empties itself because the network
      // blinked is worse than one showing a slightly old list.
      setState({
        error:
          (cause as { message?: string })?.message || "Something went wrong.",
      });
    })
    .finally(() => {
      inflight = null;
      lastRefreshAt = Date.now();
      setState({ refreshing: false });
    });
  inflight = run;
  return run;
}

/** React binding. Re-renders only when the store object actually changes. */
export function useMyApps(): MyAppsState {
  return useSyncExternalStore(subscribeMyApps, getMyAppsState, getMyAppsState);
}

/**
 * Test seam: forget everything, including the fact that storage was read.
 *
 * The store outlives a component, which is the point — but it also outlives a
 * test, so a suite that mounts the page repeatedly would otherwise see the
 * previous case's apps and skip the fetch it was asserting on.
 */
export function resetMyAppsStoreForTests(): void {
  state = EMPTY_STATE;
  inflight = null;
  lastRefreshAt = 0;
  bootstrapped = false;
  listeners.clear();
}
